import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SalesSubmit() {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    } else if (!user.is_admin) {
      toast.error('Only admins can submit sales');
      navigate('/dashboard');
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(
        `${API_URL}/api/sales`,
        { amount: parseFloat(amount), description },
        { headers: getAuthHeader() }
      );
      
      toast.success('Sale submitted for verification!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit sale');
    } finally {
      setLoading(false);
    }
  };

  if (!user || !user.is_admin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-8 text-foreground hover:text-primary"
          data-testid="back-to-dashboard-btn"
        >
          <ArrowLeft className="mr-2" /> Back to Dashboard
        </Button>

        <div className="wm-card p-8 rounded-lg card-shadow border border-primary/20">
          <h1 className="text-3xl font-playfair font-bold text-foreground mb-6">Submit Sale for Verification</h1>
          
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="sales-submit-form">
            <div>
              <Label htmlFor="amount" className="text-foreground">Sale Amount (USD)</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
                className="mt-1 bg-input border-border text-foreground"
                placeholder="1000.00"
                data-testid="amount-input"
              />
            </div>

            <div>
              <Label htmlFor="description" className="text-foreground">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                rows={4}
                className="mt-1 bg-input border-border text-foreground"
                placeholder="Provide details about this sale..."
                data-testid="description-input"
              />
            </div>

            <div className="bg-muted/20 border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Note:</strong> All sales submissions are reviewed by our admin team. 
                Please provide accurate information and supporting details. Verified sales will be added to your total.
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
              data-testid="submit-sale-btn"
            >
              {loading ? 'Submitting...' : 'Submit for Verification'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
